import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import dayjs from 'dayjs';
import 'dayjs/locale/tr';
import Pagination from '@/components/pagination';
import { Meeting, FilterType, SortField, SortState } from './past-meetings.types';
import { Button, Checkbox } from '@/components';
import '@/assets/styles/views/coach/past-meetings.scss';
import { useNavigate } from 'react-router';
import { NavigationPath } from '@/router/navigation-path.ts';
import { useApi } from '@/api/useApi.tsx';
import { useMutationProvider, useQueryProvider } from '@/hooks';
import { QUERY_KEYS } from '@/constants/QueryKeys.ts';
import { getMonthDateRange } from '@/utility';
import { endDateTimezone, INTERACTION_STATUS_ICON, REPORT_STATUS } from '@/constants';
import { useSizes } from '@/utility/responsive.ts';
import ClassNames from 'classnames';
import clsx from 'clsx';
import CircleLink from '@/assets/svg/link-circle-blue.svg';
import { useFeatureFlag } from 'configcat-react';
import { BackupRoomStudentInvitationRequest } from '@dopingtech/live_expert_bff_api_ts';
import { toast } from 'react-toastify';

const ITEMS_PER_PAGE = 10;

const PastMeetings: React.FC = () => {
	const [activeFilter, setActiveFilter] = useState<FilterType>('ALL');
	const [currentPage, setCurrentPage] = useState(1);
	const { isDesktop } = useSizes();
	const [sortState, setSortState] = useState<SortState>({
		field: 'date',
		direction: 'desc'
	});
	const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
	const [selectedDate, setSelectedDate] = useState(dayjs());
	const { InteractionControllerApi, BackupRoomControllerApi } = useApi();

	const [isSendedBackupLink, setIsSendedBackupLink] = useState(false);

	const { value: isActiveFromOutsideMeetLink } = useFeatureFlag('isActiveFromOutsideMeetLink', false);

	const sendBackupRoomInvitationToStudentMutation = useMutationProvider((data: BackupRoomStudentInvitationRequest) =>
		BackupRoomControllerApi.sendBackupRoomInvitationToStudent({ studentId: data?.studentId })
	);

	const getMeetingType = () => {
		if (activeFilter === 'ALL') return undefined;
		return activeFilter;
	};

	const getSortParams = () => {
		if (!sortState.field) return undefined;

		const sortFieldMap = {
			date: 'interactionStartDate',
			rating: 'rating',
			report: 'submittedReportStatus'
		};

		const sortField = sortFieldMap[sortState.field];
		return sortField ? [`${sortField},${sortState.direction}`] : undefined;
	};

	const { data: pastMeetingData } = useQueryProvider(
		[QUERY_KEYS.findExpertInteractions, activeFilter, selectedDate?.toString(), currentPage, sortState],
		() =>
			InteractionControllerApi.findExpertInteractions(
				getMonthDateRange(selectedDate)?.startDate,
				getMonthDateRange(selectedDate, undefined, endDateTimezone)?.endDate,
				getMeetingType(),
				'COACHING',
				currentPage - 1,
				ITEMS_PER_PAGE,
				getSortParams()
			),
		{
			select: ({ data }) => data.data,
			refetchOnWindowFocus: true
		}
	);

	const datePickerRef = useRef<HTMLDivElement | null>(null);
	const navigate = useNavigate();

	const handleSort = (field: SortField) => {
		setSortState(prevState => ({
			field,
			direction: prevState.field === field && prevState.direction === 'asc' ? 'desc' : 'asc'
		}));
		setCurrentPage(1);
	};

	const totalPages = pastMeetingData?.totalPages || 0;

	const getRatingColor = useCallback((rating: number): string => {
		if (rating >= 4) return '#FFD012';
		if (rating === 3) return '#E67E22';
		return 'var(--danger-text)';
	}, []);

	const renderRatingStars = useCallback(
		(rating: number) => {
			const ratingColor = getRatingColor(rating);
			return [...Array(5)].map((_, index) => (
				<i
					key={index}
					className={`icons-star ${index < rating ? '' : 'inactive'}`}
					style={index < rating ? { color: ratingColor } : undefined}
				/>
			));
		},
		[getRatingColor]
	);

	const handleFilterChange = useCallback(
		(newFilter: FilterType) => {
			if (newFilter === activeFilter) return;

			setActiveFilter(newFilter);
			setCurrentPage(1);
		},
		[activeFilter]
	);

	const onSendLinkToStudent = async (appointment: any) => {
		const entity: BackupRoomStudentInvitationRequest = {};

		entity.studentId = appointment?.studentId;
		//entity.meetingId = appointment?.interactionId;

		const result = await sendBackupRoomInvitationToStudentMutation.mutateAsync(entity);

		if (result) {
			setIsSendedBackupLink(true);

			setTimeout(() => {
				setIsSendedBackupLink(false);
			}, 2000);
		}

		toast.success('Görüşme linki öğrenciye gönderildi!');
	};

	const getSortIcon = (field: SortField) => {
		if (sortState.field === field) {
			return <i className={`icons-alt-arrow-${sortState.direction === 'asc' ? 'down' : 'up'}`} />;
		}
		return <i className="icons-alt-arrow-down" />;
	};

	useEffect(() => {
		const handleClickOutside = (event: MouseEvent): void => {
			if (datePickerRef.current && !datePickerRef.current.contains(event.target as Node)) {
				setIsDatePickerOpen(false);
			}
		};

		document.addEventListener('mousedown', handleClickOutside);
		return () => {
			document.removeEventListener('mousedown', handleClickOutside);
		};
	}, []);

	const monthOptions = useMemo(() => {
		const options = [];
		const currentDate = dayjs();

		for (let i = 0; i < 12; i++) {
			const date = currentDate.subtract(i, 'month');
			options.push({
				value: date.format('YYYY-MM'),
				label: date.locale('tr').format('MMMM YYYY')
			});
		}
		return options;
	}, []);

	const toggleDatePicker = () => {
		setIsDatePickerOpen(!isDatePickerOpen);
	};

	const handleMonthSelect = (date: string) => {
		setSelectedDate(dayjs(date));
		setIsDatePickerOpen(false);
		setCurrentPage(1);
	};

	const onClickMeeting = (meeting: Meeting) => () => {
		navigate(NavigationPath.coachPastMeetingsDetail, {
			state: { meeting, interactionId: meeting?.id }
		});
	};

	const getSubmittedReportInfo = report => {
		if (report === REPORT_STATUS.COMPLETED) {
			return (
				<span className={`report-badge completed`}>
					<i className="icons-check-circle" />
					<span className="badge-text">Tamamlandı</span>
				</span>
			);
		}

		if (report === REPORT_STATUS.INCOMPLETE) {
			return (
				<span className={`report-badge missing`}>
					<i className="icons-danger-circle" />
					<span className="badge-text">Eksik Rapor</span>
				</span>
			);
		}
	};

	const renderPastMeetingTable = () => {
		if (!pastMeetingData?.content || pastMeetingData.content.length === 0) {
			return (
				<div className="empty-state">
					<p>Bu dönemde görüşme bulunmamaktadır.</p>
				</div>
			);
		}

		if (isDesktop) {
			return (
				<>
					{pastMeetingData.content.map(meeting => (
						<div
							key={meeting.id}
							className={`meeting-row ${meeting?.meetingType}`}
							onClick={onClickMeeting(meeting)}>
							<div className="col-student">
								<img
									src={meeting?.studentAvatarUrl}
									alt={meeting?.studentName}
								/>
								<div className="student-info">
									<div className="name">{`${meeting?.studentName} ${meeting?.studentSurname}`}</div>
									<div className="grade">{meeting?.studentGradeName}</div>
								</div>
							</div>

							<div className="col-date">{dayjs(meeting.interactionStartDate).format('D MMMM, HH:mm')}</div>

							<div className="col-rating">
								{meeting.rating ? (
									<div className="stars">{renderRatingStars(meeting?.adminRating ? meeting?.adminRating : meeting?.rating)}</div>
								) : (
									<span className="rating-badge">Henüz puanlanmadı</span>
								)}
							</div>

							<div className="col-report">
								{getSubmittedReportInfo(meeting?.submittedReportStatus)}
								{isActiveFromOutsideMeetLink && meeting?.isActiveMeeting && (
									<div
										className={clsx('connect-alternative-link', { disabled: sendBackupRoomInvitationToStudentMutation?.isLoading || isSendedBackupLink })}
										onClick={e => {
											onSendLinkToStudent(meeting);
											e.stopPropagation();
										}}>
										<img src={CircleLink} />
										{isSendedBackupLink ? 'Link gönderildi' : 'Öğrenciye Link Gönder'}
									</div>
								)}
							</div>

							<div className="col-status">{meeting?.interactionStatusDescription}</div>
						</div>
					))}
				</>
			);
		} else {
			return (
				<div>
					{pastMeetingData.content.map(meeting => (
						<div
							key={meeting.id}
							className={`mobile-meeting-row ${meeting?.meetingType}`}
							onClick={onClickMeeting(meeting)}>
							<div className={'col-header'}>
								<div className="col-header-student">
									<img
										src={meeting?.studentAvatarUrl}
										alt={meeting?.studentName}
									/>
									<div className="student-info">
										<div className="name">{`${meeting?.studentName} ${meeting?.studentSurname}`}</div>
										<div className="grade">{meeting?.studentGradeName}</div>
									</div>
								</div>
								<div className={'col-header-information'}>
									<div className="col-header-information-date">
										<span>{dayjs(meeting.interactionStartDate).format('D MMMM, HH:mm')}</span>
									</div>
									<div className="col-header-information-rating">{meeting.rating ? <div className="stars">{renderRatingStars(meeting.rating)}</div> : null}</div>
								</div>
							</div>
							<div className={'col-body'}>
								<div className="col-status">
									<i
										className={INTERACTION_STATUS_ICON[meeting?.interactionStatus]}
										style={{
											color: INTERACTION_STATUS_ICON[meeting?.interactionStatus] === 'icons-danger-circle' ? 'rgba(241, 82, 82, 1)' : 'rgba(26, 188, 156, 1)'
										}}
									/>
									{meeting?.interactionStatusDescription}
								</div>
							</div>

							<div className={'col-footer'}>
								<div className={'col-show-report'}>
									<Button
										fullWidth={true}
										size={'small'}
										variant={'secondary'}
										style={{ background: '#fff' }}>
										<span
											className={ClassNames('show-report-text', {
												active: meeting?.submittedReportStatus === REPORT_STATUS.COMPLETED
											})}>
											{meeting?.submittedReportStatus === REPORT_STATUS.COMPLETED ? 'Değerlendirmeleri Gör' : 'Değerlendirmeleri Tamamla'}
										</span>
									</Button>
									{isActiveFromOutsideMeetLink && meeting?.isActiveMeeting && (
										<div
											className={clsx('connect-alternative-link', { disabled: sendBackupRoomInvitationToStudentMutation?.isLoading || isSendedBackupLink })}
											onClick={e => {
												onSendLinkToStudent(meeting);
												e.stopPropagation();
											}}>
											<img src={CircleLink} />
											{isSendedBackupLink ? 'Link gönderildi' : 'Öğrenciye Link Gönder'}
										</div>
									)}
								</div>
							</div>
						</div>
					))}
				</div>
			);
		}
	};

	return (
		<div className="coach-meeting-history">
			<div className="page-header">
				<h1>Geçmiş Görüşmeler</h1>
				<div
					className="date-picker"
					ref={datePickerRef}>
					<div
						className="date-trigger"
						onClick={toggleDatePicker}>
						<span className="current-month">{selectedDate.locale('tr').format('MMMM YYYY')}</span>
						<i className="icons-sort-from-top-to-bottom" />
					</div>

					{isDatePickerOpen && (
						<div className="month-dropdown">
							{monthOptions.map(month => (
								<div
									key={month.value}
									className={`month-option ${month.value === selectedDate.format('YYYY-MM') ? 'selected' : ''}`}
									onClick={() => handleMonthSelect(month.value)}>
									{month.label}
								</div>
							))}
						</div>
					)}
				</div>
			</div>

			<div className="filters-wrapper">
				<div className="filter-tabs">
					<Checkbox
						label="Tümü"
						checked={activeFilter === 'ALL'}
						onChange={() => handleFilterChange('ALL')}
						size="small"
					/>
					<Checkbox
						label="Anlık"
						checked={activeFilter === 'INSTANT'}
						onChange={() => handleFilterChange('INSTANT')}
						size="small"
					/>
					<Checkbox
						label="Randevulu"
						checked={activeFilter === 'APPOINTMENT'}
						onChange={() => handleFilterChange('APPOINTMENT')}
						size="small"
					/>
				</div>
				<div className="desktop filter-legend">
					<div className="legend-item">
						<span className="dot instant"></span>
						<span className="text">Anlık</span>
					</div>
					<div className="legend-item">
						<span className="dot scheduled"></span>
						<span className="text">Randevulu</span>
					</div>
				</div>
			</div>

			<div className="meetings-table">
				<div className="table-header desktop">
					<div className="col">Öğrenci</div>
					<div
						className="col sortable"
						onClick={() => handleSort('date')}>
						<span>Görüşme Tarihi</span>
						{getSortIcon('date')}
					</div>
					<div
						className="col sortable"
						onClick={() => handleSort('rating')}>
						<span>Puan</span>
						{getSortIcon('rating')}
					</div>
					<div
						className="col sortable"
						onClick={() => handleSort('report')}>
						<span>Değerlendirmeler</span>
						{getSortIcon('report')}
					</div>
					<div className="col">Durum</div>
				</div>

				<div className="table-body">{renderPastMeetingTable()}</div>
			</div>

			{totalPages > 1 && (
				<div className="pagination-container">
					<Pagination
						currentPage={currentPage}
						totalPages={totalPages}
						onPageChange={setCurrentPage}
					/>
				</div>
			)}
		</div>
	);
};

export default PastMeetings;
